import {useAtom} from "jotai";
import {apply_credit_monthly_payment, profile} from "@/atoms/profile";
import {codeType, CreditTypes, MpassAuthType} from "@/types_and_interfaces/types";
import DashboardClass from "@/utils/dashboard_class";
import {auth_req, edit_profile, kyc, profile_api, reset_pwd, public_reset_pwd} from "@/services/account.ts";
import useCommon from "@/hooks/common.ts";
import {useTranslation} from "react-i18next";
import CommonClass from "@/utils/common.ts";
import HaveLoggedInterface from "@/types_and_interfaces/have_logged_interface.tsx";
import Factories from "@/utils/factories.ts";
import UserInterface from "@/types_and_interfaces/user_interface.ts";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import {useCallback} from "react";
import {country_code} from "@/utils/constants.ts";
import useLoans from "@/hooks/loans.ts";
import useCalc from "@/hooks/calculator.ts";
import {get_notif_count} from "@/services/notifications.ts";
import {all_inits} from "@/atoms/common.ts";
import {set_pwd_modal_atom, set_pwd_modal_warning} from "@/atoms/modals.ts";
import * as Sentry from "@sentry/react";
import {CheckPhoneData, CheckPhoneResult} from "@/types_and_interfaces/check_phone_data_interface.ts";
import LoginData from "@/types_and_interfaces/login_data.ts";


function useAuth() {
    // TODO : change the method of setting credti line after loan is ready
    const [monthlyPay, ] = useAtom<number>(apply_credit_monthly_payment)
    const [user, setUser] = useAtom(profile)
    const loans = useLoans()
    const queryParams = new URLSearchParams(location.search);
    const debug = queryParams.get("debug")
    const [__, setOpen] = useAtom(set_pwd_modal_atom)
    const [___, setChangePwdModalWarning] = useAtom(set_pwd_modal_warning)

    const calc = useCalc()
    const [_, set_init] = useAtom(all_inits)
    const { executeRecaptcha } = useGoogleReCaptcha();

    const common_hook = useCommon()
    const {t} = useTranslation()

    const current_user = (): undefined | UserInterface => {
        return user
    }

    const isAuth = ():boolean => {
        let session_token = localStorage.getItem(CommonClass.local_storage_fields.session_token)
        return Boolean(typeof session_token === "string" && session_token?.length)
    }

    const redirectToApplyCredit = (form:FormData, creditType:CreditTypes) => {

        let form_body:{ [key: string]: any } = {}

        let amn =  form.get("amount")
        let period = form.get("period")
        let phone = user?.phone.length ? CommonClass.formatNumber(user?.phone.replace(country_code, "")) : form.get("phone")

        form_body["amn"] = amn ? amn : 0;
        form_body["period"] = period ? period : 0;
        form_body["type"] = creditType;
        form_body["phone"] = phone ? phone : "";
        form_body["monthlyPay"] = monthlyPay ? monthlyPay : 0;
        sessionStorage.setItem(CommonClass.local_storage_fields.apply_credit_form_body, JSON.stringify(form_body))
    }
    
    const setUserBlueprint = (user_data:UserInterface) =>{
        if (typeof user_data === "undefined") return
        let type:codeType = ""
        let subtract

        let storage_log_data = localStorage.getItem(CommonClass.local_storage_fields.login_data)
        if(typeof storage_log_data === "string" && storage_log_data.length) {
            let obj = JSON.parse(storage_log_data)

            if(obj.hasOwnProperty("type")) {
                type = obj.type
            }
        };

        let val

        if(user_data?.email.length && type === "email") {
            val = user_data?.email
        } else if (user_data?.phone.length && type === "phone") {
            val = user_data?.phone
        } else {
            val = user_data?.phone.length ? user_data?.phone : user_data?.email
        }

        if(val.length > 9) {
            subtract = 3
        } else {
            subtract = 2
        }

        let firstThree = val.substring(0, subtract);
        let lastThree = val.substring(val.length - subtract);
        let maskedVal = firstThree + "***" + lastThree;

        val = maskedVal

        let user_blueprint:HaveLoggedInterface = {
            value: val,
            has_pwd: user_data?.is_pass_code_set,
            has_email: user_data?.is_email_set,
            type
        }
        
        localStorage.setItem(CommonClass.local_storage_fields.have_logged_data, JSON.stringify(user_blueprint))
        localStorage.removeItem(CommonClass.local_storage_fields.login_data)
    }

    const updateUserPendingLoans = async () => {
        await loans.updPendingLoans()
        
    }
    
    const openKYCModal = (status:any, pwdError?:boolean) => {
        eventEmitter.emit(
            CommonClass.ModalEvents.GoToKYC,
            status,
            pwdError ? ()=> {
                setOpen(true)
                setChangePwdModalWarning(t("set_new_pwd_warning"))
            } : null
        )
    }
    
    const small_update = async ():Promise<UserInterface> => {
        const req = await profile_api().get_me();
        let user_data:any = {}
        
        if (req.status === 200 && req.response?.message === "ok" && req.response.hasOwnProperty("data")) {
            user_data = Factories.user_factory(req.response.data);
            setUser(user_data);
            CommonClass.sentry_user = user_data;
        }

        return user_data
    }
    
    const auth_ping = async () => {
        await get_notif_count();
    }
    
    const updateProfile = useCallback(async (options?:{pwdError?:boolean, fromLogin?:boolean}) => {
        const req = await profile_api().get_me();
        if (req.status === 200 && req.response?.message === "ok" && req.response.hasOwnProperty("data")) {
            let user_data = Factories.user_factory(req.response.data);

            setUser(user_data);
            CommonClass.sentry_user = user_data;
            setUserBlueprint(user_data);
            // TODO: remove it from update when websocket is ready
            
            eventEmitter.emit(CommonClass.CustomEvents.upd_loans)
            updateUserPendingLoans();
            await calc.update_calculator_limits()
            localStorage.removeItem(CommonClass.local_storage_fields.login_data);
            sessionStorage.removeItem(CommonClass.local_storage_fields.check_phone_data);
            let is_login_page = false;

            Object.entries(DashboardClass.login_routes).forEach((obj) => {
                if (typeof obj[1] === "string" && window.location.href.includes(obj[1])) {
                    is_login_page = true;
                    return;
                }
            });

            if (user_data.is_pass_code_set) {
                const apply_credit_form_body = sessionStorage.getItem(CommonClass.local_storage_fields.apply_credit_form_body)
                const user_phone = CommonClass.formatNumber(user_data.phone.replace(country_code, ""))

                if(apply_credit_form_body !== null && user_phone.length) {
                    const data = JSON.parse(apply_credit_form_body) || {}

                    if(data.hasOwnProperty("phone")) {
                        // alert("kikimiki1")
                        if(data.phone !== user_phone) sessionStorage.removeItem(CommonClass.local_storage_fields.apply_credit_form_body)
                    }
                }

                if(options?.fromLogin) eventEmitter.emit(CommonClass.CustomEvents.redirect_to, DashboardClass.dashboard_routes.Home);

                if(user_data.auth_type !== "ByMPass") {
                    if(
                        apply_credit_form_body === null &&
                        user_data.permissions.is_kyc_allowed &&
                        localStorage.getItem(CommonClass.local_storage_fields.login_kyc_checking_active) === "true"
                    ) {
                        openKYCModal(user_data.kyc_general_status, user_data.permissions.is_pass_code_modifiable ? options?.pwdError : false)
                    } else if(
                        apply_credit_form_body === null &&
                        !user_data.permissions.is_kyc_allowed &&
                        localStorage.getItem(CommonClass.local_storage_fields.login_kyc_checking_active) === "true"
                    ){
                        if(user_data.permissions.is_pass_code_modifiable) {
                            setOpen(true)
                            setChangePwdModalWarning(t("set_new_pwd_warning"))
                        }
                    }

                    localStorage.removeItem(CommonClass.local_storage_fields.login_kyc_checking_active)
                }
            }

            if (is_login_page && !user_data.is_pass_code_set) {
                eventEmitter.emit(CommonClass.CustomEvents.redirect_to, DashboardClass.login_routes.LoginStep3);
            }
            
            
        } else {
            if(user === undefined) {
                localStorage.removeItem(CommonClass.local_storage_fields.session_token);
                // alert("Macapuca1")
                // return
                eventEmitter.emit(CommonClass.CustomEvents.redirect_to, DashboardClass.login_routes.LoginStep1);
            }

            let timeout = setTimeout(() => {

                if(debug==="true") console.info("setTimeout useAuth updateProfile")

                eventEmitter.emit(CommonClass.CustomEvents.redirect_to, DashboardClass.login_routes.LoginStep1);
                clearTimeout(timeout);
            }, 2000);

            const currentURLRoot = window.location.origin;
            const url = `${currentURLRoot}${DashboardClass.login_routes.LoginStep1}`;
            window.open(url, '_self');
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
            
        }
        
    }, [user, monthlyPay, setUserBlueprint, updateUserPendingLoans]);
    
    const getUserNaming = ():string => {
        if(!user) return ""

        if(user.email.length && !user.phone.length && !user.first_name.length) {
            return user.email
        } else if(!user.email.length && user.phone.length && !user.first_name.length){
            return user.phone
        } else if(user.email.length && user.phone.length && !user.first_name.length) {
            return user.phone
        } else if(user.first_name.length) {
            return user.first_name
        } else {
            return ""
        }
    }
    
    const logOut = async () => {
        // localStorage.setItem(CommonClass.local_storage_fields.session_calculator_values, JSON.stringify(session_data))
        localStorage.removeItem(CommonClass.local_storage_fields.session_token)
        localStorage.removeItem(CommonClass.local_storage_fields.session_calculator_values)
        sessionStorage.removeItem(CommonClass.local_storage_fields.apply_credit_form_body)
        setUser(undefined)
        CommonClass.sentry_user = undefined
        eventEmitter.emit(CommonClass.CustomEvents.remove_contract)
        set_init(false)
        await auth_req().logout()
        CommonClass.navigateTo(DashboardClass.login_routes.LoginStep1)
    }

    const auth = async (pwd:string, user_id?: string, scenario?: "scenario1") => {

        if(!pwd.length) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("complete_field_err"))
            
            return
        }

        window.dispatchEvent(common_hook.setLoadTrue)

        let r_token:string | undefined  = undefined

        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }

        const req = await profile_api().auth(pwd, r_token, user_id)

        if(req?.status === 400) {
            if(scenario === "scenario1") {
                const getCheckPhoneData = (): CheckPhoneData | null => {
                    const stored = sessionStorage.getItem(CommonClass.local_storage_fields.check_phone_data);
                    if (stored) {
                        try {
                            return JSON.parse(stored) as CheckPhoneData;
                        } catch {
                            return null;
                        }
                    }
                    return null;
                }

                if(!getCheckPhoneData()?.can_reset_by_phone && !getCheckPhoneData()?.can_reset_by_email) {
                    let storage_log_data = localStorage.getItem(CommonClass.local_storage_fields.login_data)

                    if (storage_log_data != null) {

                        const data: LoginData = Boolean(storage_log_data) ? JSON.parse(storage_log_data) : {}

                        if(typeof data?.value === "string" && data?.value.length) {

                            await loginPhoneSt1(data.value).then((otp_res) => {

                                if(!otp_res.error.length) {
                                    eventEmitter.emit(CommonClass.CustomEvents.redirect_to, `${DashboardClass.login_routes.LoginStep2}&isPin=false&isAttemptsErr=true`);
                                }
                            })
                        }
                    }
                } else {
                    eventEmitter.emit(CommonClass.CustomEvents.redirect_to, DashboardClass.login_routes.ResetPin);
                }

                window.dispatchEvent(common_hook.setLoadFalse)
                return
            }
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("pwd_error"))
            window.dispatchEvent(common_hook.setErrTrue)
            window.dispatchEvent(common_hook.setLoadFalse)
            return
        }else if(req?.status === 500) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("error_message"))
            window.dispatchEvent(common_hook.setErrTrue)
            window.dispatchEvent(common_hook.setLoadFalse)
            return
        }else if(req?.status === 429) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("att_limit"))
            window.dispatchEvent(common_hook.setErrTrue)
            window.dispatchEvent(common_hook.setLoadFalse)
            return
        }else if(req?.status === 200 && req?.response.hasOwnProperty("message") && req.response.message === "ok") {
            CommonClass.init_auth = true
            window.dispatchEvent(common_hook.setErrFalse)
            window.dispatchEvent(common_hook.setLoadFalse)
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, "")
            localStorage.setItem(CommonClass.local_storage_fields.session_token, req.response.session_token)

            await updateProfile({fromLogin: true})
            // eventEmitter.emit(CommonClass.CustomEvents.redirect_to, DashboardClass.dashboard_routes.Home)
            
        }else {
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text,  t("error_message"))
            // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
            window.dispatchEvent(common_hook.setErrTrue)
            window.dispatchEvent(common_hook.setLoadFalse)
        }
    }
    
    const authMpass = async (x:MpassAuthType) => {
        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }
        
        await auth_req().mpass(r_token, x)
        
    }

    const getProfile = () => {
        return user
    }

    const resetPwdSt1 = async (type:codeType):Promise<boolean> => {
        let enable = false

        let value = type === "phone" ? user?.phone : user?.email

        if(!value?.length) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("complete_field_err"))
            return enable
        }

        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }

        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, true)
        const res = type === "email" ? await reset_pwd().by_email1(value, r_token) : await reset_pwd().by_phone1(value, r_token)
        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, false)

        if (res?.status === 200 && res?.message) {
            enable = true
            // common_hook.updateSnackbar("success", t(res?.message))
        }

        if(res?.status !== 200 && res?.message) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t(res?.message))
        }


        return enable
    }

    const resetPwdSt1NotLogged = async (type: codeType, value: string, user_id: string): Promise<boolean> => {
        let enable = false

        if (!value?.length) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("complete_field_err"))
            return enable
        }

        if (!user_id?.length) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
            return enable
        }

        if (type === "email" && !CommonClass.isEmailValid(value)) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_email_err"))
            return enable
        }
        if (type === "phone" && !CommonClass.phoneNumberValidateFull(value)) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_nr_err"))
            return enable
        }

        let r_token: string | undefined = undefined
        if (CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }

        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, true)
        const res = type === "email"
            ? await public_reset_pwd().by_email1(value, user_id, r_token)
            : await public_reset_pwd().by_phone1(value, user_id, r_token)
        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, false)

        if (res?.status === 200 && res?.message) {
            enable = true
        }

        if (res?.status !== 200 && res?.message) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t(res?.message))
        }

        return enable
    }

    const resetPwdSt2NotLogged = async (value: string, otp: string, passcode: string, type: codeType, user_id: string): Promise<boolean> => {
        let enable = false
        let enable_req = true
        let r_token: string | undefined = undefined

        if (CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }

        eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, "")
        eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, "")
        eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, false)
        eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, false)

        if (otp.length !== 4) {
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("incomplete_otp"))
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, true)
            enable_req = false
        }

        if (!passcode || !passcode.length) {
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("empty_pwd_error"))
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            enable_req = false
        }

        if (!value.length || typeof value !== "string") {
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("error_message"))
            return enable
        }

        if (!user_id?.length) {
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("error_message"))
            return enable
        }

        if (!enable_req) return enable

        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, true)
        const res = type === "email"
            ? await public_reset_pwd().by_email2(value, otp, passcode, user_id, r_token)
            : await public_reset_pwd().by_phone2(value, otp, passcode, user_id, r_token)
        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, false)

        if (res?.status === 200 && res?.message) {
            enable = true
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "success", t(res?.message))
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, "")
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, "")
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, false)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, false)
        }

        if (res?.status !== 200) {
            enable = false
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("wrong_data"))
        }

        return enable
    }

    const resetPwdSt2 = async ( value:any, otp:string, passcode: any, type: codeType ):Promise<boolean> => {
        let enable = false
        let enable_req = true
        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }
        
        eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text,  "")
        eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text,  "")
        eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, false)
        eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, false)
        
        // if(typeof device_uid !== "string") {
        //     // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
        //     eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text,  t("error_message"))
        //     return enable
        // }

        if(otp.length !== 4) {
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text,  t("incomplete_otp"))
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, true)
            enable_req = false
        }

        if(!passcode && !passcode.length){
            // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("empty_pwd_error"))
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text,  t("empty_pwd_error"))
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            enable_req = false
        }

        if(!value.length || typeof value !== "string") {
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("error_message"))
            
            return enable
        }

        let body = {
            passcode,
            otp,
            [type]: value,
        }

        if(!enable_req) return enable


        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, true)
        const res = type === "email" ? await reset_pwd().by_email2(body, r_token) : await reset_pwd().by_phone2(body, r_token)
        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, false)
        
        if (res?.status === 200 && res?.message) {
            enable = true
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "success", t(res?.message))
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text,  "")
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text,  "")
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, false)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, false)
            
            await updateProfile().then(() => {

                let timeout = setTimeout(() => {
                    
                    if(debug === "true") console.info("setInterval resetPwdSt2")
                    
                    logOut()
                    clearTimeout(timeout)
                }, 1000)
            })


        }

        if(res?.status !== 200) {
            enable = false
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("wrong_data"))
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("wrong_data"))
        }

        return enable
    }
    
    const resetEmailSt1 = async (value:string):Promise<boolean> => {
        let enable = false
        
        if(!value.length) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("complete_field_err"))
            return enable
        }
        
        if(!CommonClass.isEmailValid(value)) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_email_err"))
            return enable
        }

        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }
        
        eventEmitter.emit(CommonClass.CustomEvents.set_edit_profile_load, true)
        const res = await edit_profile().email1(value, r_token)
        eventEmitter.emit(CommonClass.CustomEvents.set_edit_profile_load, false)
        
        if (res?.status === 200 && res?.message) {
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, false)
            enable = true
        }
        
        if(res?.status !== 200 && res?.message) {
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t(res?.message))
        }
        
        return enable
    }
    
    const resetEmailSt2 = async (email:string, otp:string):Promise<boolean> => {
        let enable = false
        
        if(!email.length) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("complete_field_err"))
            return enable
        }
        
        if(!CommonClass.isEmailValid(email)) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_email_err"))
            return enable
        }
        
        if(otp.length !== 4) {
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, true)
            // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("incomplete_otp"))
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text,  t("incomplete_otp"))
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, true)
            return enable
        }

        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }
        
        eventEmitter.emit(CommonClass.CustomEvents.set_edit_profile_load, true)
        const res = await edit_profile().email2(email, otp, r_token)
        eventEmitter.emit(CommonClass.CustomEvents.set_edit_profile_load, false)
        
        if (res?.status === 200 && res?.message) {
            enable = true
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "success", t(res?.message))
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, false)
            await updateProfile()
        }
        
        if(res?.status !== 200 && res?.message) {
            
            if(res?.status === 400) {
                eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("otp_err"))
            } else {
                eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t(res?.message))
            }
            
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, true)
            
        }
        
        return enable
    }
    
    const resetPhoneSt1 = async (value:string):Promise<boolean> => {
        let enable = false
        
        if(!value.length) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("complete_field_err"))
            return enable
        }
        
        if(!CommonClass.phoneNumberValidate(value)) {
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_nr_err"))
            return enable
        }

        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }
        
        eventEmitter.emit(CommonClass.CustomEvents.set_edit_profile_load, true)
        const res = await edit_profile().phone1(value, r_token)
        eventEmitter.emit(CommonClass.CustomEvents.set_edit_profile_load, false)
        
        if (res?.status === 200 && res?.message) {
            enable = true
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, false)
        }
        
        if(res?.status !== 200 && res?.message) {
            
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t(res?.message))
        }
        
        return enable
    }
    
    const resetPhoneSt2 = async (phone:string, otp:string):Promise<boolean> => {
        let enable = false
        
        if(!phone.length) {
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("complete_field_err"))
            return enable
        }
        
        if(!CommonClass.phoneNumberValidate(phone)) {
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, true)
            window.dispatchEvent(common_hook.setErrTrue)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_nr_err"))
            return enable
        }
        
        if(otp.length !== 4) {
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, true)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("incomplete_otp"))
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, true)
            return enable
        }

        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }
        
        eventEmitter.emit(CommonClass.CustomEvents.set_edit_profile_load, true)
        const res = await edit_profile().phone2(phone, otp, r_token)
        eventEmitter.emit(CommonClass.CustomEvents.set_edit_profile_load, false)
        
        if (res?.status === 200 && res?.message) {
            enable = true
            eventEmitter.emit(CommonClass.CustomEvents.set_err_pwd, false)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, false)
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "success", t(res?.message))
            await updateProfile()
        }
        
        if(res?.status !== 200 && res?.message) {
            
            if(res?.status === 400) {
                eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("otp_err"))
            } else {
                eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t(res?.message))
            }
            
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_err_otp, true)
            
        }
        
        return enable
    }
    
    const getRecaptchaToken = async () => {
        try {
            if(CommonClass.general_settings.recaptcha.is_enabled && executeRecaptcha) {
                return await executeRecaptcha('homepage');
            } else {
                return undefined
            }
            
        } catch (e) {
            Sentry.captureException(e, { tags: { section: "recaptcha", action: "get_token_auth" } });
            return undefined
        }
    }

    const startFaceLiveness = async() => {
        let id:undefined | string = undefined
        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, true)

        const res = await kyc().face_liveness_start()

        eventEmitter.emit(CommonClass.CustomEvents.set_req_load, false)

        if (res?.status === 200 && res?.response.message === "ok"){
            if(res.response?.transaction_id){
                id = res.response?.transaction_id
            }
        }

        if(res?.status !== 200 && res?.message) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t(res?.message))
        }


        return id
    }

    const finishFaceLiveness = async(id:string) => {

        let is_good:boolean = false

        // eventEmitter.emit(CommonClass.CustomEvents.set_req_load, true)
        const res = await kyc().face_liveness_finish(id)
        // eventEmitter.emit(CommonClass.CustomEvents.set_req_load, false)

        if (res?.status === 200 && res?.response.message === "ok"){
            is_good = true
        }

        if(res?.status !== 200 && res?.message) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t(res?.message))
        }

        return is_good
    }

    const loginEmailSt1 = async (field: string):Promise<{error: string}> => {

        let error:string = ""
        let token: string | undefined = undefined

        if (!CommonClass.isEmailValid(field)) {
            error = t("valid_email_err")
            return {error}
        }

        if (CommonClass.general_settings.recaptcha.is_enabled) {
            if (!executeRecaptcha) {
                error = t("error_message")
                return {error}
            }
            const newToken = await executeRecaptcha('auth');
            token = newToken;
        }

        const req = await auth_req().email_st_1(field, token)

        if (req?.status === 429 || req?.status === 403) {
            error = t("att_limit")
            return {error}

        } else if (req?.status === 500) {
            error = t("error_message")
            return {error}

        } else if (req?.status === 400) {
            error = t("valid_email_err")
            return {error}
        } else {
            if(req?.status !== 200) {
                error = t("error_message")
                return {error}
            }
        }

        if (req?.error) {

            error = t("error_message")
            return {error}
        }

        return {error}
    }

    const loginPhoneSt1 = async (phoneNumber: string):Promise<{error: string}> => {
        let error:string = ""
        let token: string | undefined = undefined;

        if (phoneNumber.length == 0) {
            error = t("complete_field_err")
            return {error}
        }

        if (CommonClass.general_settings.recaptcha.is_enabled) {
            if (!executeRecaptcha) {
                error = t("error_message")
                return {error}
            }
            const newToken = await executeRecaptcha('auth');
            token = newToken;
        }

        const req = await auth_req().phone_st_1(phoneNumber, token)

        if (req?.status === 429 ) {

            error = t("att_limit")
            return {error}

        }  else if (req?.status === 403) {

            error = t("you_are_a_bot")
            return {error}

        } else if (req?.status === 400) {

            error = t("valid_nr_err")
            return {error}

        }else if (req?.status === 500) {

            error = t("error_message")
            return {error}

        } else {

            if(req?.status !== 200) {
                error = t("error_message")
                return {error}
            }

        }

        return {error}
    }

    const checkUserPhoneBeforeLogin = async (field: string, phone: string, finalToken?: string): Promise<CheckPhoneResult> => {
        const result: CheckPhoneResult = {
            data: null,
            error: ""
        }

        if (!CommonClass.phoneNumberValidate(field)) {
            result.error = t("valid_nr_err")
            return result
        }

        let token: string | undefined = undefined

        if (CommonClass.general_settings.recaptcha.is_enabled) {
            if (!executeRecaptcha) {
                result.error = t("error_message")
                return result
            }
            const newToken = await executeRecaptcha('auth');
            token = newToken;
        }

        token = finalToken ?? token;

        const req = await auth_req().check_phone(phone.replace(country_code, ""), token)

        if (req?.status === 200) {
            const checkPhoneData: CheckPhoneData = req.response.data
            sessionStorage.setItem(CommonClass.local_storage_fields.check_phone_data, JSON.stringify(checkPhoneData))
            result.data = checkPhoneData
            return result
        }

        if (req?.status === 429) {
            result.error = t("att_limit")
            return result
        }

        if (req?.status === 403) {
            result.error = t("you_are_a_bot")
            return result
        }

        if (req?.status === 400) {
            result.error = t("valid_nr_err")
            return result
        }

        if (req?.status === 500) {
            result.error = t("error_message")
            return result
        }

        return result
    }

    const sendLoginOTP = async (pin:string, data:any, pwdError?:boolean):Promise<boolean> => {
        let token:string | undefined = undefined

        if(CommonClass.general_settings.recaptcha.is_enabled) {
            if (!executeRecaptcha) {
                return false
            }
            const newToken = await executeRecaptcha('homepage');
            token = newToken;
        }

        const req = data.type === "email" ?
            await auth_req().email_st_2( pin, data.value, token) :
            await auth_req().phone_st_2( pin, data.value, token)

        if(req?.status === 429) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("att_limit"))
            return false
        }

        if(req?.status === 400) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("otp_err"))

            return false
        }

        if(req?.status === 500) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text,  t("error_message"))

            return false
        }

        if(req?.status === 200 && typeof req.response === "object" && req.response?.message === "ok" && req.response?.session_token) {
            CommonClass.init_auth = true
            localStorage.setItem(CommonClass.local_storage_fields.session_token, req.response.session_token)
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text,  "")
            let timeout = setTimeout(() => {

                if(debug === "true") console.info("setInterval StepTwoForm")

                if(isAuth()){
                    updateProfile({pwdError: pwdError})
                }

                clearTimeout(timeout)

            }, 500)

            return true
        }

        if(req?.status === 403) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("error_message"))

            return false
        }

        return false
    }

    const resendOtpUniversalFunction = async (data:any):Promise<boolean> => {

        let token: string | undefined = undefined

        if(!data?.value || !data?.value.length) return false

        if (CommonClass.general_settings.recaptcha.is_enabled) {
            if (!executeRecaptcha) {
                return false;
            }
            const newToken = await executeRecaptcha('auth');
            token = newToken;
        }

        const req = data.type === "email" ? await auth_req().email_st_1(data?.value, token) : await auth_req().phone_st_1(data?.value, token)

        if(req?.status === 429) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("att_limit"))
            return false
        }

        if(req?.error) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
            return false
        }

        if(req?.response.hasOwnProperty("message") && typeof req?.response.message === "string" && req?.response.message === "ok" ) {
            let currentDate = new Date();
            currentDate.setSeconds(currentDate.getSeconds() + 30);

            let newData = {
                type:data?.type,
                interval:currentDate,
                value: data.value
            }

            localStorage.setItem( CommonClass.local_storage_fields.login_data, JSON.stringify(newData))
            return true
        }

        return false
    }

    return {
        isAuth,
        redirectToApplyCredit,
        auth,
        authMpass,
        logOut,
        updateProfile,
        getProfile,
        setUserBlueprint,
        resetPwdSt1,
        resetPwdSt1NotLogged,
        resetPwdSt2,
        resetPwdSt2NotLogged,
        getRecaptchaToken,
        getUserNaming,
        startFaceLiveness,
        finishFaceLiveness,
        resetEmailSt1,
        resetEmailSt2,
        resetPhoneSt1,
        resetPhoneSt2,
        small_update,
        current_user,
        auth_ping,
        loginEmailSt1,
        checkUserPhoneBeforeLogin,
        loginPhoneSt1,
        sendLoginOTP,
        resendOtpUniversalFunction,
        isRecaptchaReady: !CommonClass.general_settings?.recaptcha?.is_enabled || !!executeRecaptcha
    }
}


export default useAuth